import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { setAuthToken, removeAuthToken, getStoredUser } from '../utils/auth';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already authenticated
  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const storedUser = getStoredUser();
      
      if (storedUser) {
        // Verify token by making a request to /me endpoint
        try {
          const response = await api.get('/api/auth/me');
          setUser(response.data);
          setIsAuthenticated(true);
        } catch (error) {
          // Token is invalid or expired
          removeAuthToken();
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      setError('Authentication check failed');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // On initial load, check if user is authenticated
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login function
  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/api/auth/login', credentials);
      
      const { access_token, refresh_token, user } = response.data;
      
      // Store tokens and user info
      setAuthToken(access_token, refresh_token, user);
      
      setUser(user);
      setIsAuthenticated(true);
      
      toast.success('Login successful');
      return true;
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed';
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    removeAuthToken();
    setUser(null);
    setIsAuthenticated(false);
    toast.info('You have been logged out');
  };

  // Token refresh function (used by api interceptor)
  const refreshToken = async () => {
    try {
      const response = await api.post('/api/auth/refresh');
      const { access_token } = response.data;
      
      // Update stored access token
      const storedUser = getStoredUser();
      if (storedUser) {
        setAuthToken(access_token, storedUser.refresh_token, storedUser.user);
      }
      
      return access_token;
    } catch (error) {
      // If refresh fails, logout
      logout();
      throw error;
    }
  };

  // Update user profile
  const updateUserProfile = (updatedUser) => {
    setUser(prevUser => ({ ...prevUser, ...updatedUser }));
    
    // Update stored user data
    const storedUser = getStoredUser();
    if (storedUser) {
      setAuthToken(
        storedUser.access_token, 
        storedUser.refresh_token, 
        { ...storedUser.user, ...updatedUser }
      );
    }
  };

  // Context value
  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    checkAuth,
    refreshToken,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};